/**
 * Per-item batch settlement for clinical writes (#2236).
 *
 * Batch flows (specimens, symptoms, monitoring drafts) used to fire N
 * independent requests through Promise.all and throw one opaque error: a
 * single failure discarded the results of requests the server had ALREADY
 * accepted, and a whole-form retry re-created them — duplicate clinical
 * records. Until the API grows idempotent bulk endpoints (dashboard-api
 * #648/#931 family), every batch must settle per item so callers keep the
 * accepted ids and retry only the failures.
 */

export interface BatchOutcome<TItem, TResult> {
  succeeded: { item: TItem; result: TResult }[];
  failed: { item: TItem; error: unknown }[];
}

/**
 * A persisted-write receipt: the exact content that was accepted (signature)
 * and what the server gave back (result — typically carrying the created
 * record id). Binding retries to receipts instead of bare keys is what makes
 * an EDITED retry safe: same logical row + changed content means "update the
 * accepted record", never "skip" and never "create a duplicate".
 */
export type BatchAcceptedWrite<TResult> = {
  signature: string;
  result: TResult;
};

export type BatchRetryLedger<TResult = unknown> = {
  accepted: Map<string, BatchAcceptedWrite<TResult>>;
};

export function createBatchRetryLedger<
  TResult = unknown,
>(): BatchRetryLedger<TResult> {
  return { accepted: new Map() };
}

/**
 * Sentinel for runs that performed no persisted write (e.g. a blank field
 * with nothing to clear). No-writes are never recorded in the ledger, so a
 * later edit of the same logical row always executes.
 */
export const BATCH_NO_WRITE: unique symbol = Symbol("batch-no-write");
export type BatchNoWrite = typeof BATCH_NO_WRITE;

/**
 * Thrown when at least one item of a batch failed. Carries the full
 * per-item outcome so catch sites can persist the accepted ids for the
 * retry window instead of resubmitting the entire batch. It extends Error,
 * so existing generic catch/alert paths keep working unchanged.
 */
export class PartialBatchError<
  TItem = unknown,
  TResult = unknown,
> extends Error {
  readonly outcome: BatchOutcome<TItem, TResult>;

  constructor(message: string, outcome: BatchOutcome<TItem, TResult>) {
    super(message);
    this.name = "PartialBatchError";
    this.outcome = outcome;
  }
}

/**
 * Run one request per item and settle them all — a thrown request never
 * discards sibling successes. `run` should THROW (or reject) for a failed
 * item and resolve with the created/updated record otherwise.
 */
export async function settleBatch<TItem, TResult>(
  items: readonly TItem[],
  run: (item: TItem, index: number) => Promise<TResult>,
): Promise<BatchOutcome<TItem, TResult>> {
  const settled = await Promise.allSettled(
    items.map((item, index) => Promise.resolve().then(() => run(item, index))),
  );
  const outcome: BatchOutcome<TItem, TResult> = { succeeded: [], failed: [] };
  settled.forEach((entry, index) => {
    const item = items[index]!;
    if (entry.status === "fulfilled") {
      outcome.succeeded.push({ item, result: entry.value });
    } else {
      outcome.failed.push({ item, error: entry.reason });
    }
  });
  return outcome;
}

/**
 * Settle a batch against a receipt ledger owned by the calling hook:
 *
 * - an item whose key AND signature match an accepted write is skipped —
 *   that exact content is already persisted;
 * - an item whose key matches but whose signature differs runs again with
 *   the prior receipt, so the runner can UPDATE the accepted record instead
 *   of creating a duplicate;
 * - runs that return BATCH_NO_WRITE are not recorded, so a later edit of
 *   that logical row executes normally.
 *
 * The ledger clears once a save settles with zero failures — receipts only
 * need to survive the retry window of a partially-failed save.
 */
export async function settleBatchWithLedger<TItem, TResult>(
  items: readonly TItem[],
  identify: (item: TItem, index: number) => { key: string; signature: string },
  run: (
    item: TItem,
    index: number,
    prior: TResult | undefined,
  ) => Promise<TResult | BatchNoWrite>,
  ledger: BatchRetryLedger<TResult>,
): Promise<BatchOutcome<TItem, TResult | BatchNoWrite>> {
  const identified = items.map((item, index) => ({
    item,
    index,
    id: identify(item, index),
  }));
  const pending = identified.filter(
    ({ id }) => ledger.accepted.get(id.key)?.signature !== id.signature,
  );

  const settled = await Promise.allSettled(
    pending.map(({ item, index, id }) =>
      Promise.resolve().then(() =>
        run(item, index, ledger.accepted.get(id.key)?.result),
      ),
    ),
  );

  const outcome: BatchOutcome<TItem, TResult | BatchNoWrite> = {
    succeeded: [],
    failed: [],
  };
  settled.forEach((entry, i) => {
    const { item, id } = pending[i]!;
    if (entry.status === "fulfilled") {
      outcome.succeeded.push({ item, result: entry.value });
      if (entry.value !== BATCH_NO_WRITE) {
        ledger.accepted.set(id.key, {
          signature: id.signature,
          result: entry.value,
        });
      }
    } else {
      outcome.failed.push({ item, error: entry.reason });
    }
  });

  if (outcome.failed.length === 0) {
    // Everything the user asked for is persisted (or was a no-op): the next
    // save is a fresh session, not a retry of this one.
    ledger.accepted.clear();
  }

  return outcome;
}

/**
 * Settle a batch and throw a PartialBatchError when anything failed —
 * the common shape for mutation functions that must reject on failure
 * while preserving the accepted items for retry-only-failures.
 */
export async function settleBatchOrThrow<TItem, TResult>(
  items: readonly TItem[],
  run: (item: TItem, index: number) => Promise<TResult>,
  describe: (outcome: BatchOutcome<TItem, TResult>) => string,
): Promise<TResult[]> {
  const outcome = await settleBatch(items, run);
  if (outcome.failed.length > 0) {
    throw new PartialBatchError(describe(outcome), outcome);
  }
  return outcome.succeeded.map((s) => s.result);
}

/**
 * Ledger-aware variant of settleBatchOrThrow: skip identical accepted
 * writes, hand prior receipts to edited retries, throw PartialBatchError
 * when anything failed. Returns the results of runs that performed a write.
 */
export async function settleBatchWithLedgerOrThrow<TItem, TResult>(
  items: readonly TItem[],
  identify: (item: TItem, index: number) => { key: string; signature: string },
  run: (
    item: TItem,
    index: number,
    prior: TResult | undefined,
  ) => Promise<TResult | BatchNoWrite>,
  ledger: BatchRetryLedger<TResult>,
  describe: (outcome: BatchOutcome<TItem, TResult | BatchNoWrite>) => string,
): Promise<TResult[]> {
  const outcome = await settleBatchWithLedger(items, identify, run, ledger);
  if (outcome.failed.length > 0) {
    throw new PartialBatchError(describe(outcome), outcome);
  }
  return outcome.succeeded
    .map((s) => s.result)
    .filter((result): result is TResult => result !== BATCH_NO_WRITE);
}
